import React, { useEffect, useRef, useState } from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import { useAppDispatch } from "../../../hooks/typedHooks";
import { setCredentials } from "../authSlice";
import { useLoginMutation, useCheckLoginMutation } from "../authApiSlice";
import LoginImg from "../../../assets/bg-rockys.png";
import Logo from "../../../shared/design/Logo";
import { KeyIcon } from "@heroicons/react/24/outline";

const Login = () => {
  const userRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  // Where to return after a successful login (e.g. "Log in to book" on a tour).
  const from = (location.state as { from?: string } | null)?.from;

  const [login, { isLoading }] = useLoginMutation();
  const [sendCheckLogin] = useCheckLoginMutation();

  useEffect(() => {
    if (userRef.current) userRef.current.focus();
  }, []);

  useEffect(() => {
    setErrMsg("");
  }, [email, password]);

  const performLogin = async (loginEmail: string, loginPassword: string) => {
    try {
      const data = await login({
        email: loginEmail,
        password: loginPassword,
      }).unwrap();
      dispatch(setCredentials({ accessToken: data.token }));

      const res = await sendCheckLogin("");
      if ("data" in res && res.data.data.loginStatus === true) {
        setEmail("");
        setPassword("");
        navigate(from ?? "/");
        navigate(0);
      } else {
        setErrMsg(
          "Unable to complete login request. Please note that third party cookies are required while we are in beta. Please check your browser's settings to confirm third party cookies are allowed."
        );
      }
    } catch (err: unknown) {
      const fetchErr = err as {
        status?: number | string;
        data?: { message?: string };
      };
      if (!fetchErr.status) setErrMsg("No Server Response");
      else if (fetchErr.status === 400) setErrMsg("Missing Email or Password");
      else if (fetchErr.status === 401)
        setErrMsg("Invalid email/password combination.");
      else setErrMsg(fetchErr.data?.message ?? "");
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    performLogin(email, password);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Image side */}
      <div className="relative hidden flex-col justify-between overflow-hidden p-10 text-white lg:flex">
        <img
          src={LoginImg}
          alt="Mountain vista"
          className="wn-ken absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[rgba(19,58,69,0.78)] to-[rgba(19,58,69,0.35)]" />
        <Logo tone="light" className="relative" />
        <div className="relative">
          <h2 className="mb-3 font-serif text-[40px] font-normal leading-[1.08]">
            Welcome back,
            <br />
            wanderer.
          </h2>
          <p className="max-w-[380px] text-[16px] text-[#dde7e3]">
            Your next horizon is waiting. Sign in to manage bookings, save
            trips, and pick up where you left off.
          </p>
        </div>
        <div className="relative font-mono text-[12px] tracking-[0.04em] text-white/80">
          ⌖ 51.2°N 115.6°W · BANFF, AB
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-8 md:p-10">
        <div className="w-full max-w-[400px]">
          <div className="mb-10 text-right text-[13.5px] text-muted">
            New here?{" "}
            <NavLink
              to="/signup"
              className="font-bold text-secondary-400 no-underline"
            >
              Create an account
            </NavLink>
          </div>

          <h1 className="mb-1.5 font-serif text-[38px] font-normal tracking-[-0.01em]">
            Sign in
          </h1>
          <p className="mb-8 text-[15px] text-muted">Good to see you again.</p>

          {errMsg && (
            <div className="mb-[18px] rounded-[11px] bg-hard/10 px-3.5 py-3 text-[13.5px] font-semibold text-hard">
              {errMsg}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <label className="mb-1.5 block text-[12px] font-bold tracking-[0.04em] text-muted">
              EMAIL
            </label>
            <input
              ref={userRef}
              type="email"
              className="wn-field mb-[18px]"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-[12px] font-bold tracking-[0.04em] text-muted">
                PASSWORD
              </label>
              <NavLink
                to="/login"
                className="text-[12.5px] font-semibold text-secondary-400 no-underline"
              >
                Forgot?
              </NavLink>
            </div>
            <div className="relative mb-[18px]">
              <input
                type={showPwd ? "text" : "password"}
                className="wn-field pr-[46px]"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPwd((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[13px] text-muted-300"
              >
                {showPwd ? "hide" : "show"}
              </button>
            </div>

            <label className="mb-6 flex cursor-pointer select-none items-center gap-2.5 text-[13.5px] text-muted">
              <input
                type="checkbox"
                className="h-4 w-4 accent-secondary-400"
                checked={keepSignedIn}
                onChange={(e) => setKeepSignedIn(e.target.checked)}
              />
              Keep me signed in
            </label>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-[13px] bg-secondary-400 py-4 text-[16px] font-bold text-white transition-colors hover:bg-ink disabled:opacity-60"
            >
              {isLoading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3.5 text-[12.5px] text-muted-300">
            <div className="h-px flex-1 bg-line" />
            OR
            <div className="h-px flex-1 bg-line" />
          </div>
          <button
            type="button"
            disabled
            title="Social sign-in isn't available in this demo"
            className="flex w-full cursor-not-allowed items-center justify-center gap-2.5 rounded-[13px] border border-line bg-white py-3.5 text-[15px] font-semibold text-ink opacity-70"
          >
            <span className="font-extrabold text-secondary-400">G</span>{" "}
            Continue with Google
          </button>

          {/* Demo credentials — revealed on hover/focus for portfolio visitors */}
          <div className="mt-6 flex justify-center">
            <div className="group relative">
              <button
                type="button"
                className="flex items-center gap-2 rounded-full border border-line bg-white px-3.5 py-2 text-[12.5px] font-semibold text-muted transition-colors hover:border-secondary-400 hover:text-ink"
              >
                <KeyIcon className="h-4 w-4" /> Demo login — hover for credentials
              </button>
              <div className="pointer-events-none absolute bottom-full left-1/2 mb-2.5 w-[230px] -translate-x-1/2 rounded-xl bg-ink p-3.5 text-left opacity-0 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.6)] transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100">
                <div className="mb-2 font-mono text-[10.5px] font-medium tracking-[0.1em] text-primary-500">
                  DEMO ACCOUNT
                </div>
                <div className="flex justify-between gap-3 text-[12.5px]">
                  <span className="text-muted-300">email</span>
                  <span className="font-mono text-sand">loulou@example.com</span>
                </div>
                <div className="mt-1 flex justify-between gap-3 text-[12.5px]">
                  <span className="text-muted-300">password</span>
                  <span className="font-mono text-sand">test1234</span>
                </div>
                <div className="absolute left-1/2 top-full h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-ink" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
